import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import MainLayout from './components/layout/MainLayout';
import { CircularProgress, Box } from '@mui/material';

// Eagerly loaded pages (small, frequently accessed)
import LoginPage from './pages/LoginPage';
// import RegisterPage from './pages/RegisterPage';
// Self-registration is controlled by the SERVER (ALLOW_SELF_REGISTRATION,
// defaulting to closed on a governed instance). Commenting out this route only
// removed the link -- POST /api/auth/register stayed open to anyone who knew
// the URL, on an instance holding governed audio, until Sprint 21 closed it.
// Do not treat a commented-out route as an access control.
import HomePage from './pages/HomePage';

// Lazy loaded pages (larger bundles, loaded on demand)
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const RecordingWorkspacePage = lazy(() => import('./pages/RecordingWorkspacePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const TranscriptComparisonPage = lazy(() => import('./pages/TranscriptComparisonPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

// Loading fallback for lazy loaded components
function PageLoader() {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="50vh"
    >
      <CircularProgress />
    </Box>
  );
}

// Protected route wrapper component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Admin route wrapper component
function AdminRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.is_admin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes (no layout) */}
      <Route path="/login" element={<LoginPage />} />
      {/* Self-registration disabled - admin creates accounts via Admin Dashboard */}
      {/* <Route path="/register" element={<RegisterPage />} /> */}

      {/* Protected routes (with layout) */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
                  {/* UI-6: the working surface for one recording. The project
                      page finds; this one works. */}
                  <Route
                    path="/projects/:projectId/recordings/:audioFileId"
                    element={<RecordingWorkspacePage />}
                  />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/search" element={<SearchResultsPage />} />
                  <Route path="/compare/:audioFileId" element={<TranscriptComparisonPage />} />
                  <Route
                    path="/admin"
                    element={
                      <AdminRoute>
                        <AdminDashboard />
                      </AdminRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </AuthProvider>
  );
}

export default App;
